import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FAQS } from "@/pages/FaqPage";

/**
 * Route-aware SEO: sets document.title + meta description per page.
 * All copy is compliance-checked — no yield/return/investment-product claims.
 */

const DEFAULT_TITLE = "Mavula Investment — Open Doors to Home Ownership in Southern Africa";
const DEFAULT_DESC =
  "Mavula Investment Ltd (UK-registered company No. 17368822) helps Southern Africans at home and in the diaspora buy property through a structured purchase pathway — deposit secures your plot, instalments over time, title deed in your name.";

const ROUTES = {
  "/": [DEFAULT_TITLE, DEFAULT_DESC],
  "/about": ["About Mavula Investment — UK-Registered Property Purchase Pathway",
    "Who we are: Mavula Investment Ltd, a UK-registered company (No. 17368822) opening doors to home ownership in Botswana, Zambia, Namibia, South Africa and Zimbabwe."],
  "/why-mavula": ["Why Mavula — Built for the Diaspora",
    "Why thousands of Southern Africans abroad struggle to buy property back home — and how Mavula's structured purchase pathway fixes it."],
  "/how-it-works": ["How It Works — Deposit, Instalments, Title Deed | Mavula",
    "The Mavula pathway in plain steps: choose your plot, secure it with a deposit, pay the balance in instalments, and receive the title deed in your name."],
  "/projects": ["Property Projects in Southern Africa | Mavula Investment",
    "Browse current Mavula property projects across Botswana, Zambia, Namibia, South Africa and Zimbabwe."],
  "/countries": ["Countries We Serve | Mavula Investment",
    "Mavula operates in Botswana, Zambia, Namibia, South Africa and Zimbabwe — with purchase pathways designed for locals and diaspora buyers."],
  "/legal-status": ["Legal & Regulatory Status | Mavula Investment Ltd",
    "Our regulatory position, stated plainly: what Mavula is, what we are not, and the registrations we hold and are pursuing. No fine print."],
  "/how-your-money-is-held": ["How Your Money Is Held | Mavula Investment",
    "Exactly where your money sits at every stage of your purchase pathway — and the protections around it."],
  "/protections": ["Your Protections | Mavula Investment",
    "The safeguards built into every Mavula purchase: contracts, title registration, and what happens if things go wrong."],
  "/risk-realities": ["The Honest Risks | Mavula Investment",
    "Buying property has real risks. Here they are, in plain language — because you deserve honesty before you commit."],
  "/scam-warning": ["Scam Warning & How to Verify Us | Mavula Investment",
    "How to confirm you are dealing with the real Mavula Investment Ltd — and the red flags of property scams targeting the diaspora."],
  "/safety": ["Safety & Trust Centre | Mavula Investment",
    "Everything we do to keep your purchase safe: verification, contracts, secure payments and transparent terms."],
  "/security": ["Security at Mavula | Data & Payment Protection",
    "How we protect your personal data and payments: encryption, UK data protection standards, and secure payment partners."],
  "/faq": ["Frequently Asked Questions | Mavula Investment",
    "Straight answers on deposits, instalments, title deeds, eligibility, currencies and how the purchase pathway works."],
  "/policies": ["Policies | Mavula Investment",
    "Our policies in one place: privacy, refunds, complaints, and how we handle your information."],
  "/team": ["Meet the Team | Mavula Investment",
    "The people behind Mavula Investment — building trustworthy property pathways for Southern Africa."],
  "/stories": ["Customer Stories | Mavula Investment",
    "Real stories from Southern Africans buying property at home and abroad through the Mavula pathway."],
  "/impact": ["Our Impact | Mavula Investment",
    "What Mavula's purchase pathways mean in practice: families housed, wealth built at home, communities strengthened."],
  "/roadmap": ["Company Roadmap | Mavula Investment",
    "Where Mavula is heading: upcoming projects, new countries, and features our members have asked for."],
  "/builders": ["Verified Builders | Mavula Investment",
    "Meet the verified builders who construct Mavula homes — vetted, contracted and accountable."],
  "/compare": ["Compare Your Options | Mavula Investment",
    "How the Mavula pathway compares with bank mortgages, remittances to family, and informal arrangements."],
  "/site-visits": ["Book a Site Visit | Mavula Investment",
    "See before you commit: book a visit to a Mavula project site, in person or via video call."],
  "/office-hours": ["Office Hours — Talk to a Human | Mavula Investment",
    "Book a call with the Mavula team. Real people, straight answers, no pressure."],
  "/register-interest": ["Register Your Interest | Mavula Investment",
    "Tell us where you want to own and your budget — we'll match you to the right project and walk you through the pathway."],
  "/waiting-list": ["Join the Private Waiting List | Mavula Investment",
    "Be first in line for upcoming projects. Private, no obligation, no payment."],
  "/founding-member": ["Become a Founding Member | Mavula Investment",
    "Join the Pula Founding Members: £3/month to support the platform and get priority access to new projects."],
  "/investors": ["Institutional & Compliance Enquiries | Mavula Investment",
    "For institutions, legal teams and regulators: our corporate details, regulatory roadmap, and how to reach us."],
  "/pula": ["The Pula Ecosystem | Mavula Investment",
    "Mavula is part of the Pula family of services for Southern Africans — community, training and property working together."],
  "/learn": ["Pula Learn — Training for UK Care Workers (Pre-Launch)",
    "Practical, affordable training for UK care workers, built by people who understand care. CPD accreditation applied for."],
  "/learn/bundles": ["Care Training Bundles | Pula Learn (Pre-Launch)",
    "Learning Disability & Autism, Safeguarding Adults and induction bundles for UK care workers. Launching after CPD accreditation."],
  "/verify": ["Verify a Certificate | Pula Learn",
    "Check whether a Pula Learn certificate is genuine using its unique certificate ID."],
};

export function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const match =
      ROUTES[pathname] ||
      (pathname.startsWith("/projects/") ? ["Project Details | Mavula Investment", "View this Mavula property project: location, plots, prices and the purchase pathway."] : null) ||
      (pathname.startsWith("/learn/course/") ? ["Course Preview | Pula Learn", "Preview this Pula Learn care training course: modules, quizzes and progress tracking."] : null);
    const [title, desc] = match || [DEFAULT_TITLE, DEFAULT_DESC];
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", desc);

    // FAQ structured data (injected only on /faq, built from the same source the page renders)
    document.getElementById("faq-ld-json")?.remove();
    if (pathname === "/faq") {
      const script = document.createElement("script");
      script.id = "faq-ld-json";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
      document.head.appendChild(script);
    }
  }, [pathname]);

  return null;
}
